import { useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import BookingDialog from "@/components/BookingDialog";
import { Scissors, Clock, Sparkles } from "lucide-react";

const services = [
  {
    category: "Taglio",
    icon: Scissors,
    items: [
      {
        name: "Taglio Deluxe",
        price: "37€",
        age: "12+ anni",
        description: "Esecuzione del taglio, esaminando accuratamente la morfologia del viso, con clipper e/o forbice, doppio shampoo prima e dopo, pre-asciugatura, finish a scelta, nebulizzazione profumo EAU DE PARFUM e massaggio (con un massaggiatore specifico per il cuoio capelluto) con Tonico vitalizzante che aiuta a migliorare l'ancoraggio dei capelli e l'elasticità del cuoio capelluto, rinforzando e nutrendo la struttura del follicolo.",
        featured: true,
        ageHighlight: true,
      },
      {
        name: "Taglio Tradizionale",
        price: "31€",
        age: "12+ anni",
        description: "Esecuzione del taglio, esaminando accuratamente la morfologia del viso, con clipper e/o forbice, doppio shampoo prima e dopo, pre-asciugatura, finish a scelta e nebulizzazione profumo.",
        ageHighlight: true,
      },
    ],
  },
  {
    category: "Barba",
    icon: Sparkles,
    items: [
      {
        name: "Barba Superior Xaikonix",
        price: "50€",
        description: "Sistemazione della barba con forbice e macchinetta, o rasatura completa pelo e contropelo utilizzando lame 'FEATHER' leader a livello mondiale, shampoo, olio e balsamo specifici, 2 panni uno caldo/freddo, dopobarba alcolico o in crema e nebulizzazione profumo EAU DE PARFUM. Inoltre, verrà eseguito un trattamento-viso rivitalizzante con prodotti cosmetici della linea XAIKONIX utilizzando il vaporizzatore che garantirà una maggiore efficacia dei prodotti.",
        featured: true,
      },
      {
        name: "Barba Deluxe",
        price: "32€",
        description: "Sistemazione della barba con forbice e clipper, o rasatura completa pelo e contropelo, se necessario. Utilizzo di shampoo, olio e balsamo specifici, 2 panni uno caldo e uno freddo, dopobarba alcolico o in crema. Il servizio sarà svolto con il vaporizzatore, strumento che facilita la rasatura, riduce il rischio di irritazioni e favorisce l'assorbimento dei prodotti.",
      },
      {
        name: "Barba Tradizionale",
        price: "27€",
        description: "Sistemazione della barba con forbice e clipper, o rasatura completa pelo e contropelo. Utilizzo di olio e balsamo specifici, 1 panno caldo, dopobarba alcolico o in crema.",
      },
    ],
  },
  {
    category: "Esperienze",
    icon: Clock,
    items: [
      {
        name: "Servizio Relax Kairos",
        price: "140€",
        description: "Questo è il servizio per eccellenza ideale per regalarsi o regalare 90 minuti di puro relax. Aperitivo di benvenuto, scelta della musica durante l'intero servizio, esecuzione del TAGLIO DELUXE e della BARBA SUPERIOR XAIKONIX, prodotto finish in omaggio.",
        featured: true,
        duration: "90 min",
      },
      {
        name: "Analisi Cute con Microcamera",
        price: "15€",
        description: "L'analisi con la microcamera ci permette di valutare lo stato della cute e del capello ed individuare tutte le possibili problematiche non visibili ad occhio nudo.",
      },
    ],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" },
};

export const ServiziPage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);

  return (
    <div className="min-h-screen bg-kairos-black" data-testid="servizi-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6" data-testid="servizi-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.p
            className="text-kairos-gold text-xs uppercase tracking-[0.3em] mb-4 font-accent"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            I Miei Servizi
          </motion.p>
          <motion.h1
            className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-6 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Scopri l'eccellenza
          </motion.h1>
          <motion.p
            className="text-white/60 max-w-xl mx-auto text-base md:text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Servizi pensati per valorizzare il tuo tempo e offrirti un'esperienza unica
          </motion.p>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-6" data-testid="services-list">
        <div className="max-w-6xl mx-auto">
          {services.map((category, categoryIndex) => (
            <motion.div
              key={category.category}
              className="mb-20"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1 }}
            >
              <div className="flex items-center gap-4 mb-10">
                <category.icon className="w-6 h-6 text-kairos-gold" />
                <h2 className="font-serif text-2xl md:text-3xl text-white">
                  {category.category}
                </h2>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <div className="space-y-6">
                {category.items.map((service, index) => (
                  <motion.div
                    key={service.name}
                    {...fadeUp}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    className={`service-card relative bg-kairos-gray/30 border p-8 md:p-10 ${
                      service.featured 
                        ? "border-kairos-gold/30" 
                        : "border-white/5"
                    }`}
                    data-testid={`service-item-${categoryIndex}-${index}`}
                  >
                    {service.featured && (
                      <div className="absolute top-0 right-0 bg-kairos-gold text-kairos-black text-xs uppercase tracking-wider px-4 py-1 font-bold">
                        Premium
                      </div>
                    )}
                    
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-serif text-xl md:text-2xl text-white mb-2">
                          {service.name}
                        </h3>
                        <div className="flex items-center gap-4 text-sm">
                          {service.age && (
                            <span className={`${service.ageHighlight ? 'bg-kairos-gold/20 text-kairos-gold px-3 py-1 font-semibold text-base border border-kairos-gold/30' : 'text-white/50'}`}>
                              Età minima: {service.age}
                            </span>
                          )}
                          {service.duration && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {service.duration}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-kairos-gold text-3xl md:text-4xl font-serif">
                        {service.price}
                      </span>
                    </div>
                    
                    <p className="text-white/60 leading-relaxed">
                      {service.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-kairos-gray/20" data-testid="servizi-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6 tracking-tight">
              Pronto per la tua esperienza?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-10">
              Prenota il tuo appuntamento e vivi il momento giusto per te.
            </p>
            <button
              onClick={() => setBookingOpen(true)}
              className="bg-kairos-gold text-kairos-black px-12 py-5 text-sm uppercase tracking-[0.2em] font-bold hover:bg-white transition-all duration-300"
              data-testid="servizi-book-btn"
            >
              Prenota Ora
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} />
    </div>
  );
};

export default ServiziPage;
